import {
    alpha,
    Box,
    Button,
    Grid,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { accentGreen, lightGrey2, white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import dayjs from 'dayjs';
import React, { FC, useState } from 'react';
import { Edit } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { EnterpriseTemplate } from '../types';
import EditTemplateModal from './EditTemplateModal';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import TemplateArchiveModal from './TemplateArchiveModal';
import CopyTemplateModal from './CopyTemplateModal';
import { stripHtml } from 'string-strip-html';
import AddIcon from '@mui/icons-material/Add';
import AddCompanyModal from './AddCompanyModal';

type SingleTemplateRowProps = {
    item: EnterpriseTemplate;
};

const SingleTemplateRow: FC<SingleTemplateRowProps> = ({ item }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [addCompanyModal, setAddCompanyModal] = useState(false);
    const isDisabled = item?.total_companies > 0 && item?.status == 0;

    const navigate = useNavigate();
    return (
        <>
            <ListItem
                sx={{
                    bgcolor: white,
                    borderRadius: 8,
                    p: 3,
                }}
            // onClick={() => {
            //     item.status === 0 && navigate(`/enterprise/template/${item.id}`)
            // }}
            >
                <Grid container alignItems="center" sx={{
                    justifyContent: {
                        xs: 'center',
                        sm: 'space-between',
                    },
                }}>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        textAlign={{ xs: 'center', sm: 'left' }}
                        sx={{ wordBreak: 'break-all' }}
                    >
                        <ListItemText
                            style={{ margin: '0' }}
                            disableTypography
                            primary={
                                <Typography
                                    variant="h6"
                                    component="p"
                                    color="secondary"
                                    fontSize="19px"
                                    fontWeight="bold"
                                    sx={{ marginBottom: '2px' }}
                                >
                                    {item.name}
                                </Typography>
                            }
                            secondary={
                                <Stack spacing={1} mb={2}>
                                    <Typography
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '14px',
                                            lineHeight: '14px',
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 2,
                                            
                                        }}
                                    >
                                        {stripHtml(item?.description ?? '').result}
                                    </Typography>
                                </Stack>
                            }
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={2}
                        display="flex"
                        justifyContent={{ xs: 'center', sm: 'flex-start' }}
                        alignItems="flex-start"
                        pt={1}
                    >
                        <Box px={2} py={1} borderRadius="5px">
                            <Typography
                                textTransform="capitalize"
                                fontWeight={medium}
                                sx={{ fontSize: '14px', lineHeight: '14px' }}
                            >
                                <b>Billing to:</b> {item.payment_method === 0 ? 'Employer' : 'Enterprise'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid spacing={5} item xs={12} sm={2} pt={1}
                        display="flex"
                        sx={{ paddingBottom: { xs: 3, sm: 1 } }}
                        justifyContent={{ xs: 'center', sm: 'flex-start' }}
                        alignItems="flex-start">
                        <Typography textTransform="capitalize"
                            fontWeight={medium}
                            sx={{ fontSize: '14px', lineHeight: '14px' }}>
                            {item.total_companies === 0 ? (
                                <>No attached company</>
                            ) : item.total_companies === 1 ? (
                                <> <b>1 </b>{' '} attached company </>
                            ) : (
                                <> <b>{item.total_companies}{' '}</b> attached companies </>
                            )}
                        </Typography>
                    </Grid>
                    <Stack direction="row" spacing={3} alignSelf='flex-end'>
                        <Grid>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/enterprise/template/${item.id}`);
                                    // setShowEditModal(true)
                                }}
                                size="small"
                                color="primary"
                                variant="contained"
                                startIcon={<Edit />}
                            >
                                Edit
                            </Button>
                        </Grid>
                        <Grid>
                            <Tooltip
                                arrow
                                title={isDisabled ? 'Template Already being used' : ''}
                                disableHoverListener={!isDisabled}
                            >
                                <span>
                                    <Button
                                        disabled={isDisabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowArchiveModal(true);
                                        }}
                                        size="small"
                                        color={item.status == 0 ? 'info' : 'success'}
                                        variant="contained"
                                        startIcon={
                                            item.status == 0 ? <ArchiveIcon /> : <UnarchiveIcon />
                                        }
                                    >
                                        {item.status == 0 ? 'Archive' : 'Active'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowCopyModal(true);
                                }}
                                size="small"
                                color="primary"
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                            >
                                Copy
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                size="small"
                                disabled={item.status !== 0}
                                onClick={() => setAddCompanyModal(true)}
                                startIcon={<AddIcon />}
                                variant="contained"
                                color='secondary'
                                sx={{
                                    height: 'fit-content',
                                    // mt: 2,
                                    width: '100%',
                                }}
                            >
                                Add Company
                            </Button>
                        </Grid>
                    </Stack>
                </Grid>
            </ListItem>
            {showEditModal && (
                <EditTemplateModal
                    handleClose={() => setShowEditModal(false)}
                    item={item}
                />
            )}
            {showCopyModal && (
                <CopyTemplateModal
                    handleClose={() => setShowCopyModal(false)}
                    item={item}
                />
            )}
            {showArchiveModal && (
                <TemplateArchiveModal
                    handleClose={() => setShowArchiveModal(false)}
                    item={item}
                    status={item?.status === 0 ? 1 : 0}
                />
            )}
            {
                addCompanyModal && (
                    <AddCompanyModal
                        templateId={item.id}
                        templateName={item.name}
                        handleClose={() => {
                            setAddCompanyModal(false);
                        }}
                        billingType={item.payment_method}
                    />
                )
            }
        </>
    );
};

export default SingleTemplateRow;
